import { useState } from "react";

// @mui material components
import Container from "@mui/material/Container";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";
import MKInput from "components/MKInput";
import MKAlert from "components/MKAlert";
import SEO from "components/SEO";

// SEO structured data
import { buildBreadcrumbSchema } from "config/structuredData";

// Material Kit 2 React examples
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import DefaultFooter from "examples/Footers/DefaultFooter";

// API
import { submitContactMessage } from "api";

// Tracking
import { trackMetaEvent } from "utils/metaEvents";

// Routes
import routes from "routes";
import footerRoutes from "footer.routes";

const CONTACT_EMAIL = "croceanfrontrentals@gmail.com";
const CONTACT_REASONS = [
  {
    icon: "event_available",
    title: "Availability and rates",
    description: "Questions about open dates, pricing, or longer stays.",
  },
  {
    icon: "airport_shuttle",
    title: "Transportation and arrivals",
    description: "Help with airport transfers, vehicles, or planning your arrival.",
  },
  {
    icon: "support_agent",
    title: "House details and special requests",
    description: "Ask about amenities, group needs, or anything else before booking.",
  },
];

function Contact() {
  const [formData, setFormData] = useState({
    email: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(null);

  const handleChange = (field) => (event) => {
    const value = event.target.value;

    setFormData((previous) => ({
      ...previous,
      [field]: value,
    }));
    setSubmitError(null);
    setSubmitSuccess(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const email = formData.email.trim();
    const message = formData.message.trim();

    if (!email || !message) {
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);

    try {
      await submitContactMessage({ email, message });
      setFormData({ email: "", message: "" });
      setSubmitSuccess("Thanks for reaching out — your message has been sent.");
      trackMetaEvent("Contact", { email });
    } catch (error) {
      setSubmitError(error.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const isFormValid = formData.email.trim() && formData.message.trim();

  return (
    <>
      <SEO
        title="Contact Us"
        description="Questions about Stalley Beach House? Contact us about availability, rates, airport transfers, vehicles and special requests for your Costa Rica beach vacation."
        path="/contact"
        imageAlt="Contact the hosts of Stalley Beach House, Costa Rica"
        schema={buildBreadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Contact", path: "/contact" },
        ])}
      />
      <DefaultNavbar routes={routes} sticky />
      <MKBox
        minHeight="30vh"
        width="100%"
        variant="gradient"
        bgColor="dark"
        sx={{
          display: "grid",
          placeItems: "center",
        }}
      >
        <Container>
          <Grid
            container
            item
            xs={12}
            lg={8}
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            sx={{ mx: "auto", textAlign: "center" }}
          >
            <MKTypography variant="h2" component="h1" color="white">
              Contact Us
            </MKTypography>
            <MKTypography variant="body1" color="white" textAlign="center" opacity={0.8} mt={1}>
              We&apos;re happy to help with availability, transportation, special requests, or any
              questions about staying at Stalley Beach House.
            </MKTypography>
          </Grid>
        </Container>
      </MKBox>
      <Card
        sx={{
          p: { xs: 2.5, md: 4 },
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 4,
          backgroundColor: ({ palette: { white }, functions: { rgba } }) => rgba(white.main, 0.86),
          backdropFilter: "saturate(200%) blur(30px)",
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        <Container maxWidth="lg">
          <MKBox textAlign="center" mb={5}>
            <MKTypography
              variant="button"
              color="info"
              fontWeight="bold"
              sx={{ textTransform: "uppercase", letterSpacing: "0.14em" }}
            >
              Get in touch
            </MKTypography>
            <MKTypography variant="h3" mt={1.5} mb={1.5}>
              We&apos;d love to hear from you
            </MKTypography>
            <MKTypography
              variant="body1"
              color="text"
              opacity={0.8}
              sx={{ maxWidth: "42rem", mx: "auto" }}
            >
              Whether you&apos;re planning dates, organizing transportation, or just want a few more
              details before booking, send us a message and we&apos;ll point you in the right
              direction.
            </MKTypography>
          </MKBox>

          <Grid container spacing={4} alignItems="stretch">
            <Grid item xs={12} md={5}>
              <MKBox
                p={{ xs: 3, md: 4 }}
                borderRadius="xl"
                height="100%"
                sx={({ palette: { white }, functions: { rgba }, boxShadows: { md } }) => ({
                  backgroundColor: rgba(white.main, 0.72),
                  border: "1px solid rgba(26, 115, 232, 0.12)",
                  boxShadow: md,
                })}
              >
                <MKTypography
                  variant="button"
                  color="info"
                  fontWeight="bold"
                  sx={{ textTransform: "uppercase", letterSpacing: "0.12em" }}
                >
                  Direct email
                </MKTypography>
                <MKTypography variant="h4" mt={1.5} mb={1.5}>
                  Contact us at this email:
                </MKTypography>
                <MKBox
                  component="a"
                  href={`mailto:${CONTACT_EMAIL}`}
                  display="inline-flex"
                  alignItems="center"
                  gap={1}
                  px={2}
                  py={1.25}
                  borderRadius="lg"
                  mb={2.5}
                  sx={({ palette: { info }, functions: { rgba } }) => ({
                    backgroundColor: rgba(info.main, 0.08),
                    color: info.main,
                  })}
                >
                  <Icon sx={{ color: "info.main" }}>mail</Icon>
                  <MKTypography variant="button" color="info" fontWeight="bold">
                    {CONTACT_EMAIL}
                  </MKTypography>
                </MKBox>

                <MKBox display="flex" alignItems="flex-start" gap={1.25} mb={2.5}>
                  <Icon sx={{ color: "info.main", mt: 0.2 }}>schedule</Icon>
                  <MKTypography variant="body2" color="text">
                    We usually reply within 12 hours.
                  </MKTypography>
                </MKBox>

                <MKTypography variant="body2" color="text" opacity={0.8} mb={3}>
                  Prefer email? Send us a note directly, or use the form to share a few details and
                  we&apos;ll reply to the address you provide.
                </MKTypography>

                <MKTypography variant="h6" mb={2}>
                  What guests usually ask about
                </MKTypography>

                <Grid container spacing={2}>
                  {CONTACT_REASONS.map(({ icon, title, description }) => (
                    <Grid item xs={12} key={title}>
                      <MKBox display="flex" alignItems="flex-start" gap={1.5}>
                        <MKBox
                          width="2.75rem"
                          height="2.75rem"
                          borderRadius="lg"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          sx={({ palette: { info }, functions: { rgba } }) => ({
                            backgroundColor: rgba(info.main, 0.1),
                            color: info.main,
                            flexShrink: 0,
                          })}
                        >
                          <Icon sx={{ fontSize: "1.35rem" }}>{icon}</Icon>
                        </MKBox>
                        <MKBox>
                          <MKTypography variant="button" display="block" fontWeight="bold">
                            {title}
                          </MKTypography>
                          <MKTypography variant="body2" color="text" opacity={0.8}>
                            {description}
                          </MKTypography>
                        </MKBox>
                      </MKBox>
                    </Grid>
                  ))}
                </Grid>
              </MKBox>
            </Grid>

            <Grid item xs={12} md={7}>
              <MKBox
                component="form"
                onSubmit={handleSubmit}
                p={{ xs: 3, md: 4 }}
                borderRadius="xl"
                height="100%"
                sx={({ palette: { white }, functions: { rgba }, boxShadows: { md } }) => ({
                  backgroundColor: rgba(white.main, 0.92),
                  border: "1px solid rgba(26, 115, 232, 0.12)",
                  boxShadow: md,
                })}
              >
                <MKTypography
                  variant="button"
                  color="info"
                  fontWeight="bold"
                  sx={{ textTransform: "uppercase", letterSpacing: "0.12em" }}
                >
                  Send a message
                </MKTypography>
                <MKTypography variant="h4" mt={1.5} mb={1.5}>
                  Or send us a message here:
                </MKTypography>
                <MKTypography variant="body2" color="text" opacity={0.8} mb={3}>
                  Tell us a little about what you need and we&apos;ll get back to you as soon as we
                  can.
                </MKTypography>

                {submitSuccess && (
                  <MKAlert color="success" sx={{ mb: 3 }}>
                    {submitSuccess}
                  </MKAlert>
                )}

                {submitError && (
                  <MKAlert color="error" sx={{ mb: 3 }}>
                    {submitError}
                  </MKAlert>
                )}

                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <MKInput
                      type="email"
                      label="Your Email Address"
                      fullWidth
                      required
                      autoComplete="email"
                      value={formData.email}
                      onChange={handleChange("email")}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <MKInput
                      label="How can we help?"
                      multiline
                      rows={6}
                      fullWidth
                      required
                      value={formData.message}
                      onChange={handleChange("message")}
                      placeholder="Please share your dates, questions, or any details that would help us respond."
                      inputProps={{ maxLength: 5000 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <MKButton
                      type="submit"
                      variant="gradient"
                      color="info"
                      size="large"
                      disabled={!isFormValid || submitting}
                      sx={{ width: { xs: "100%", sm: "auto" } }}
                    >
                      <Icon sx={{ mr: 1 }}>send</Icon>
                      {submitting ? "Sending..." : "Send Message"}
                    </MKButton>
                  </Grid>
                </Grid>

                <MKTypography variant="caption" color="text" display="block" mt={2}>
                  We&apos;ll reply to the email address you enter above.
                </MKTypography>
              </MKBox>
            </Grid>
          </Grid>
        </Container>
      </Card>
      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

export default Contact;
